import { useCallback, useEffect, useState } from 'react';
import { usePolling } from '../../hooks/usePolling';
import { endpoints, type PowerBiAccountItems, type PowerBiKind } from '../../services/api';
import { startMicrosoft, startPowerBi, useSession } from '../../store/session';
import { useSettings, type PowerBiItem } from '../../store/settings';
import { Choice, ghostButtonClass, inputClass, Section, Toggle, WidgetPage } from './controls';

const same = (a: { kind: string; id: string; workspaceId: string | null; account?: string }, b: typeof a) =>
  a.kind === b.kind && a.id === b.id && a.workspaceId === b.workspaceId && (a.account ?? '') === (b.account ?? '');

/** A ticked report's page choice (the report's first page by default). */
function PagePicker({ item, onChange }: { item: PowerBiItem; onChange: (page: { name: string; title: string } | null) => void }) {
  const [pages, setPages] = useState<Array<{ name: string; title: string }> | null>(null);
  useEffect(() => {
    endpoints
      .powerbiPages(item.id, item.workspaceId, item.account)
      .then((r) => setPages(r.pages))
      .catch(() => setPages([]));
  }, [item.id, item.workspaceId, item.account]);
  if (!pages?.length) return null;
  return (
    <select
      className={`${inputClass} ml-7 mt-1 w-auto bg-[var(--surface-strong)] py-1`}
      value={item.pageName ?? ''}
      onChange={(e) => onChange(pages.find((p) => p.name === e.target.value) ?? null)}
      aria-label={`Page of ${item.name}`}
    >
      <option value="">First page</option>
      {pages.map((p) => (
        <option key={p.name} value={p.name}>
          {p.title}
        </option>
      ))}
    </select>
  );
}

/** Every workspace's reports and dashboards, per account, to tick for the widget. */
function ItemChooser() {
  const items = useSettings((s) => s.powerbi.items);
  const update = useSettings((s) => s.update);
  const { data, loading, error } = usePolling('powerbi-items-settings', useCallback(() => endpoints.powerbiItems().then((r) => r.accounts), []), 10 * 60_000);
  if (!data) return <p className="muted text-xs">{loading ? 'Loading your Power BI workspaces…' : error}</p>;
  const several = data.length > 1;
  const toggle = (acc: PowerBiAccountItems, ws: PowerBiAccountItems['workspaces'][number], kind: PowerBiKind, it: { id: string; name: string }) => {
    const ref = { kind, id: it.id, workspaceId: ws.id, account: acc.account };
    const on = items.some((x) => same(x, ref));
    update('powerbi', {
      items: on
        ? items.filter((x) => !same(x, ref))
        : [...items, { ...ref, name: it.name, workspaceName: ws.name, accountEmail: acc.accountEmail }],
    });
  };
  const setPage = (item: PowerBiItem, page: { name: string; title: string } | null) =>
    update('powerbi', { items: items.map((x) => (same(x, item) ? { ...x, pageName: page?.name ?? '', pageTitle: page?.title ?? '' } : x)) });

  if (!data.some((a) => a.workspaces.length)) return <p className="muted text-sm">No reports or dashboards found in your Power BI workspaces.</p>;
  return (
    <div className="space-y-3">
      {data.map((acc) => (
        <div key={acc.account} className="space-y-3">
          {several && <div className="muted text-xs font-semibold uppercase tracking-wider">{acc.accountEmail ?? 'Microsoft account'}</div>}
          {acc.workspaces.map((ws) => (
            <div key={ws.id ?? 'mine'}>
              <div className="pb-1 text-sm font-medium">{ws.name}</div>
              <ul className="space-y-1 pl-1">
                {(['report', 'dashboard'] as const).flatMap((kind) =>
                  (kind === 'report' ? ws.reports : ws.dashboards).map((it) => {
                    const chosen = items.find((x) => same(x, { kind, id: it.id, workspaceId: ws.id, account: acc.account }));
                    return (
                      <li key={`${kind}:${it.id}`}>
                        <label className="flex cursor-pointer items-center gap-3 text-sm">
                          <input type="checkbox" className="accent-[var(--accent)]" checked={Boolean(chosen)} onChange={() => toggle(acc, ws, kind, it)} />
                          {it.name}
                          <span className="muted text-xs">{kind === 'report' ? 'Report' : 'Dashboard'}</span>
                        </label>
                        {chosen && kind === 'report' && <PagePicker item={chosen} onChange={(p) => setPage(chosen, p)} />}
                      </li>
                    );
                  }),
                )}
              </ul>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

/** Settings → Widgets → Power BI. */
export function PowerBITab() {
  const { config, microsoft } = useSession();
  const powerbi = useSettings((s) => s.powerbi);
  const update = useSettings((s) => s.update);
  const accounts = (microsoft?.accounts ?? []).filter((a) => a.connected);
  const without = accounts.filter((a) => !a.powerbi);
  const anyPowerBi = accounts.some((a) => a.powerbi);

  return (
    <WidgetPage
      label="Show Power BI"
      description="Your Power BI reports and dashboards, shown as they are in Power BI and kept up to date. Needs a Power BI licence (or reports in a Premium / Fabric workspace)."
      enabled={powerbi.enabled}
      onChange={(enabled) => update('powerbi', { enabled })}
    >
      <Section title="Reports and dashboards" description={powerbi.items.length > 1 ? 'With several, tabs switch between them.' : undefined}>
        {!config?.features.microsoft ? (
          <p className="muted text-sm">Microsoft sign-in isn’t available on this server.</p>
        ) : !accounts.length ? (
          <div className="flex items-center justify-between gap-3 text-sm">
            <span className="muted">Connect your work or school Microsoft account first.</span>
            <button className="shrink-0 text-[var(--accent)] hover:underline" onClick={() => startMicrosoft('link')}>
              Connect Microsoft
            </button>
          </div>
        ) : (
          <>
            {without.length > 0 && (
              <div className="space-y-2 rounded-lg border border-[var(--border)] p-3 text-sm">
                <p className="muted">Allow KCL Canvas to read your Power BI reports and dashboards (Microsoft asks you to agree once).</p>
                <div className="flex flex-wrap gap-2">
                  {without.map((a) => (
                    <button key={a.id} className={ghostButtonClass} onClick={() => startPowerBi(a.id)}>
                      Connect Power BI{without.length > 1 && a.email ? ` for ${a.email}` : ''}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {anyPowerBi && <ItemChooser />}
          </>
        )}
        {powerbi.items.length > 1 && (
          <>
            <p className="pt-2 text-sm">Move to the next one</p>
            <Choice
              options={[
                { id: '0', name: 'Never' },
                { id: '30', name: 'Every 30 s' },
                { id: '60', name: 'Every minute' },
                { id: '300', name: 'Every 5 min' },
              ]}
              value={String(powerbi.rotateSec)}
              onChange={(v) => update('powerbi', { rotateSec: Number(v) })}
              columns={4}
            />
          </>
        )}
      </Section>

      <Section title="Display">
        <p className="text-sm">Reports</p>
        <Choice
          options={[
            { id: 'page', name: 'Fit the page', description: 'The whole page in the box' },
            { id: 'width', name: 'Fit the width', description: 'Fills the box’s width' },
          ]}
          value={powerbi.fit}
          onChange={(fit) => update('powerbi', { fit })}
        />
        <Toggle label="See-through background (photos show behind)" checked={powerbi.transparent} onChange={(transparent) => update('powerbi', { transparent })} />
        <p className="pt-2 text-sm">Reload report visuals</p>
        <Choice
          options={[
            { id: '0', name: 'Never' },
            { id: '5', name: '5 min' },
            { id: '15', name: '15 min' },
            { id: '60', name: 'Hourly' },
          ]}
          value={String(powerbi.refreshMin)}
          onChange={(v) => update('powerbi', { refreshMin: Number(v) })}
          columns={4}
        />
        <p className="muted text-xs">Shows the latest data in the report’s dataset; how fresh that is depends on its refresh schedule in Power BI. Dashboards update themselves.</p>
      </Section>
    </WidgetPage>
  );
}
